import { useApiClient } from '@skylabs-digital/react-proto-kit';
import { useMemo } from 'react';
import { downloadUrlSchema, type FistoFile, fileSchema } from '../schemas/file.schema';
import { unwrap } from './unwrap';

export interface FilesQuery {
  page: number;
  limit: number;
  prefix?: string;
  status?: 'PENDING' | 'UPLOADED';
}

export interface FilesPage {
  data: FistoFile[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

/** Sólo los parámetros que el endpoint declara; un filtro vacío no viaja. */
export function buildFilesQuery(q: FilesQuery): string {
  const params = new URLSearchParams({ page: String(q.page), limit: String(q.limit) });
  if (q.prefix) params.set('prefix', q.prefix);
  if (q.status) params.set('status', q.status);
  return params.toString();
}

export function useFileActions() {
  const { connector } = useApiClient();
  // Memoizado sobre el connector: `AppFilesTable` cuelga su efecto de `list`.
  return useMemo(
    () => ({
      list: async (appId: string, q: FilesQuery): Promise<FilesPage> => {
        const res = await connector.get<unknown[]>(`apps/${appId}/files?${buildFilesQuery(q)}`);
        const data = unwrap(res).map((f) => fileSchema.parse(f));
        const meta = (res as { meta?: FilesPage['meta'] }).meta ?? {
          total: data.length,
          page: q.page,
          limit: q.limit,
          totalPages: 1,
          hasNext: false,
          hasPrev: q.page > 1,
        };
        return { data, meta };
      },
      downloadUrl: async (appId: string, fileId: string, variant?: string) => {
        const q = variant ? `?${new URLSearchParams({ variant })}` : '';
        return downloadUrlSchema.parse(
          unwrap(await connector.get<unknown>(`apps/${appId}/files/${fileId}/download-url${q}`)),
        );
      },
      remove: async (appId: string, fileId: string) => {
        unwrap(await connector.delete<unknown>(`apps/${appId}/files/${fileId}`));
      },
    }),
    [connector],
  );
}
